import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config/GameConfig';

interface ExplosionOpts {
  count?: number;
  color?: number;
  big?: boolean;
}

/**
 * Effects —— 通用粒子/震屏/飘字特效模块
 *
 * 所有特效"播完即销毁"，不持有状态，所以做成实例只是为了拿 scene 引用。
 * 用法：const fx = new Effects(this); fx.damageText(x,y,5);
 */
export default class Effects {
  scene: Phaser.Scene;
  /**
   * hit-pause 互斥锁 + 冷却 —— 同一时间只允许一个 hit-pause 在跑；触发后 400ms 内
   * 不再触发新的 hit-pause（红屏 / 飘字 / 屏抖照常播）。
   *
   * 否则连续命中会把 physics.world.timeScale 多次乘 20，setTimeout 还原顺序错乱后
   * 留下永久放大的 timeScale，整个游戏陷入慢动作，看起来像卡死。
   */
  private _hitPauseUntil = 0;

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  /**
   * 飘字合并状态：同一 mergeKey 在 lifetime 内复用同一个 Text，把数字累加 + 弹一下。
   * 用 WeakMap 让敌人对象本身作为 key —— 敌人销毁后自动 GC，不用手动清理。
   * 没有 mergeKey 的旧调用照常走"每发都新建"路径。
   */
  private _activeDamageTexts = new WeakMap<object, {
    text: Phaser.GameObjects.Text;
    accum: number;
    isCrit: boolean;
    expiresAt: number;
  }>();

  /**
   * 飘字伤害数字（"5" 这种小数字飘上去然后淡出）。isCrit=true 时金色 + 略大
   * @param mergeKey 可选 —— 通常传敌人实例。同一对象在 ~200ms 内复用同一个飘字
   *                 （伤害累加 + 弹缩）；9 弹同帧打同一只怪只显示一个数字
   */
  damageText(x: number, y: number, amount: number, isCrit = false, mergeKey?: object): void {
    const now = performance.now();
    if (mergeKey) {
      const existing = this._activeDamageTexts.get(mergeKey);
      if (existing && now < existing.expiresAt && existing.text.active) {
        existing.accum += amount;
        existing.isCrit = existing.isCrit || isCrit;
        const t = existing.text;
        t.setText(existing.isCrit ? `${existing.accum}!` : String(existing.accum));
        // 续命：再延长一段 lifetime，避免一直命中却很快消失
        existing.expiresAt = now + 200;
        // 弹一下表示有新累加（不重置位移 tween，飘字依然在向上飘）
        this.scene.tweens.add({
          targets: t,
          scale: { from: 1.5, to: 1 },
          duration: 140,
          ease: 'Back.easeOut',
        });
        return;
      }
    }

    const t = this.scene.add
      .text(x + Phaser.Math.Between(-12, 12), y, isCrit ? `${amount}!` : String(amount), {
        fontFamily: 'sans-serif',
        fontSize: isCrit ? '24px' : '18px',
        fontStyle: 'bold',
        color: isCrit ? '#ffe066' : '#ffffff',
        stroke: isCrit ? '#5a3a08' : '#aa3333',
        strokeThickness: isCrit ? 4 : 3,
      })
      .setOrigin(0.5)
      .setDepth(150);
    this.scene.tweens.add({
      targets: t,
      y: y - (isCrit ? 42 : 30),
      alpha: 0,
      scale: isCrit ? { from: 1.4, to: 1 } : { from: 1, to: 1 },
      duration: isCrit ? 650 : 500,
      onComplete: () => {
        t.destroy();
        if (mergeKey) this._activeDamageTexts.delete(mergeKey);
      },
    });
    if (mergeKey) {
      this._activeDamageTexts.set(mergeKey, {
        text: t,
        accum: amount,
        isCrit,
        expiresAt: now + 200,
      });
    }
  }

  /** 吸血回血飘字：玩家头顶 +1 ❤ 绿色字 */
  healText(x: number, y: number): void {
    const t = this.scene.add
      .text(x, y - 36, '+1 ❤', {
        fontFamily: 'sans-serif',
        fontSize: '18px',
        fontStyle: 'bold',
        color: '#7fffa0',
        stroke: '#0a3a18',
        strokeThickness: 3,
      })
      .setOrigin(0.5)
      .setDepth(195);
    this.scene.tweens.add({
      targets: t,
      y: y - 70,
      alpha: { from: 1, to: 0 },
      duration: 700,
      onComplete: () => t.destroy(),
    });
  }

  /**
   * 爆炸碎屑：在 (x,y) 喷出一圈方块粒子，向外飞、淡出、缩小
   * Boss 爆炸 (big=true) 多一圈白色冲击波 + 更强的屏幕震动
   */
  explosion(x: number, y: number, opts: ExplosionOpts = {}): void {
    const count = opts.count ?? (opts.big ? 28 : 14);
    const color = opts.color ?? 0xffaa55;
    const speedMin = opts.big ? 180 : 100;
    const speedMax = opts.big ? 380 : 220;
    const sizeMul = opts.big ? 2.2 : 1.4;

    for (let i = 0; i < count; i++) {
      const angle = (Math.PI * 2 * i) / count + Math.random() * 0.4;
      const speed = Phaser.Math.Between(speedMin, speedMax);
      const dx = Math.cos(angle) * speed * 0.4;
      const dy = Math.sin(angle) * speed * 0.4;

      const piece = this.scene.add
        .image(x, y, 'particle')
        .setTint(color)
        .setDepth(160)
        .setScale(sizeMul);

      this.scene.tweens.add({
        targets: piece,
        x: x + dx,
        y: y + dy,
        alpha: 0,
        scale: 0,
        angle: Phaser.Math.Between(-180, 180),
        duration: opts.big ? 600 : 380,
        ease: 'Cubic.easeOut',
        onComplete: () => piece.destroy(),
      });
    }

    if (opts.big) {
      const ring = this.scene.add
        .circle(x, y, 20, 0xffffff, 0)
        .setStrokeStyle(4, 0xffffff, 1)
        .setDepth(160);
      this.scene.tweens.add({
        targets: { r: 20 },
        r: 180,
        duration: 500,
        ease: 'Cubic.easeOut',
        onUpdate: (tw, tgt: { r: number }) => {
          ring.setRadius(tgt.r);
          ring.setStrokeStyle(4, 0xffffff, 1 - tw.progress);
        },
        onComplete: () => ring.destroy(),
      });
    }

    this.scene.cameras.main.shake(opts.big ? 220 : 80, opts.big ? 0.012 : 0.005);
  }

  /**
   * 玩家受击提示：v2 强化版 —— 一颗心都不能白丢
   *   - hit-pause：场景物理 / 时间缩放 0.05× 持续 80ms（视觉冻结一帧），
   *     回血时 tween 出来；命中那一瞬"咬一下时间"
   *   - 全屏径向红屏：从屏幕中央向外的 vignette，2 次脉冲 + 第 2 次更强
   *   - 玩家位置闪一道白色冲击波（小圈快速扩散）
   *   - 玩家头顶飘 -N ❤ 红字
   *   - 中等强度相机震动
   *
   * 注意：hit-pause 会暂停 Phaser 的 physics + tween 进度，但不会暂停我们这里
   * 自己创建的 tween（这些 tween 也会被冻结，因为 tweens.add 默认接 timeScale 影响）。
   * 解决：我们这里所有展示性 tween 提前指定 ignoreTimeScale: true，让它们不被
   * timeScale 0.05 拖慢，hit-pause 期间红屏 / 飘字依然正常播。
   */
  playerHurt(x: number, y: number, amount: number): void {
    // 1) hit-pause：互斥锁 + 400ms 冷却，避免叠加把 timeScale 永久放大
    const now = performance.now();
    if (now >= this._hitPauseUntil) {
      this._hitPauseUntil = now + 400; // 触发后 400ms 内不再触发
      const physicsWorld = this.scene.physics.world;
      const baseTimeScale = 1; // 始终还原成 1（不读当前值，避免被自己污染）
      // physics.world.timeScale > 1 = 慢，< 1 = 快（与 Phaser tween 反过来！）
      physicsWorld.timeScale = 20;
      // 80ms 真实时钟还原 —— scene.time 会跟着 timeScale 走，所以用 window.setTimeout
      window.setTimeout(() => {
        physicsWorld.timeScale = baseTimeScale;
      }, 80);
    }
    // 红屏 / 飘字 / 屏抖等纯视觉反馈不锁，连续受击都要播

    // 2) 全屏径向红屏：用一个全屏矩形描厚边模拟 vignette + 一个全屏纯红低 alpha 闪屏
    //    第一波：薄边 + 弱闪；第二波：粗边 + 更强闪（让"被打到"的体感像两声鼓点）
    const vignette = this.scene.add
      .rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT)
      .setStrokeStyle(64, 0xff2a3a, 1)
      .setDepth(190)
      .setScrollFactor(0)
      .setAlpha(0);
    const flash = this.scene.add
      .rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0xff2a3a, 0.3)
      .setDepth(189)
      .setScrollFactor(0)
      .setAlpha(0);

    // 双脉冲：alpha 0→1→0.4→1→0
    this.scene.tweens.add({
      targets: vignette,
      alpha: { from: 0, to: 1 },
      duration: 70,
      yoyo: true,
      repeat: 1,
      onComplete: () => vignette.destroy(),
    });
    this.scene.tweens.add({
      targets: flash,
      alpha: { from: 0, to: 0.55 },
      duration: 90,
      yoyo: true,
      onComplete: () => flash.destroy(),
    });

    // 3) 玩家位置白色冲击波（受击点的"中心化"反馈）
    const ring = this.scene.add
      .circle(x, y, 18, 0xffffff, 0)
      .setStrokeStyle(4, 0xffffff, 1)
      .setDepth(160);
    this.scene.tweens.add({
      targets: { r: 18 },
      r: 90,
      duration: 280,
      ease: 'Cubic.easeOut',
      onUpdate: (tw, tgt: { r: number }) => {
        ring.setRadius(tgt.r);
        ring.setStrokeStyle(4, 0xffffff, 1 - tw.progress);
      },
      onComplete: () => ring.destroy(),
    });

    // 4) -N ❤ 飘字（玩家头顶）
    const t = this.scene.add
      .text(x, y - 36, `-${amount} ❤`, {
        fontFamily: 'sans-serif',
        fontSize: '24px',
        fontStyle: 'bold',
        color: '#ff5566',
        stroke: '#3a0a14',
        strokeThickness: 4,
      })
      .setOrigin(0.5)
      .setDepth(195);
    this.scene.tweens.add({
      targets: t,
      y: y - 80,
      alpha: { from: 1, to: 0 },
      scale: { from: 1.6, to: 0.95 },
      duration: 800,
      ease: 'Cubic.easeOut',
      onComplete: () => t.destroy(),
    });

    // 5) 相机震动 —— 比击杀更强（200ms / 0.014）
    this.scene.cameras.main.shake(200, 0.014);
  }
}
